import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { Videocam as MeetingIcon } from '@mui/icons-material';

import { getLinkTypeLabel, getPresenceScoreColor } from '../trustCheckLogs';
import { PLATFORM_LOGO_URL } from './helpers';

// Compact MUI Chip showing the trust-check kind (ServiceNow / SDK / Adhoc / ...).
// Each kind gets a distinct MUI palette color so the row is visually scannable.
export const TrustCheckKindBadge = ({ linkType, t }) => {
    const { key, color } = getLinkTypeLabel(linkType);
    const label = t ? t(key) : key;
    return (
        <Chip
            size="small"
            color={color}
            label={label}
            variant="outlined"
            data-testid={`trust-check-kind-${linkType}`}
            sx={{ height: 22, fontWeight: 500 }}
        />
    );
};

// First-column rendering: platform logo for meetings, kind badge for trust checks.
export const RowTypeIndicator = ({ log, t }) => {
    if (log.callType === 'trust_check') {
        return <TrustCheckKindBadge linkType={log.link_type} t={t} />;
    }
    const platform = log.meeting?.platform;
    const url = PLATFORM_LOGO_URL[platform];
    if (url) {
        return (
            <Box
                component="img"
                src={url}
                alt={platform}
                sx={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }}
            />
        );
    }
    // Fallback for any meeting whose platform we don't have a logo for.
    return (
        <Tooltip title={platform || (t ? t('callLogs.unknown') : 'Unknown')}>
            <MeetingIcon color="info" />
        </Tooltip>
    );
};

// Compact MUI Chip showing the presence score result.
export const PresenceScoreChip = ({ score }) => {
    if (!score) {
        return (
            <Typography variant="body2" color="text.secondary" component="span">
                -
            </Typography>
        );
    }
    return (
        <Chip
            size="small"
            color={getPresenceScoreColor(score)}
            label={score}
            variant="filled"
            data-testid={`presence-score-${score}`}
            sx={{ height: 22, fontWeight: 500 }}
        />
    );
};

// "No certificate" muted tag — conveys that the trust check happened but the
// PDF artifact is missing. Per spec § 4: case (a/b/c) all collapse into this
// single visual treatment from the user's point of view.
export const NoCertificateTag = ({ t }) => (
    <Tooltip title={t ? t('callLogs.noCertificateTooltip') : 'Trust check completed but no signed PDF is available yet'}>
        <Chip
            size="small"
            label={t ? t('callLogs.noCertificate') : 'No certificate'}
            variant="outlined"
            data-testid="trust-check-no-certificate"
            sx={{ height: 22, fontStyle: 'italic', color: 'text.disabled', borderColor: 'divider' }}
        />
    </Tooltip>
);
